import { useEffect, useState } from "react";
import { Building2, CalendarDays, Users, Phone, Mail, Globe, ExternalLink, AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { pickHeader } from "../lib/assets";

export default function AiAirlineProfile({ code, seed, name }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(false); setData(null);
    api.get(`/airlines/${code}/article`, { timeout: 60000 })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [code]);

  if (loading) {
    return (
      <div className="border border-zinc-300 bg-white p-8 flex items-center gap-4" data-testid="ai-profile-loading">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin shrink-0" />
        <div className="text-base text-zinc-700">
          We are creating a Profile for <span className="font-700 text-zinc-900">{name || "this airline"}</span>. This can take a few seconds.
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="border border-zinc-300 bg-white p-6 text-sm text-muted-foreground" data-testid="ai-profile-error">
        Could not generate a profile for this airline right now.
      </div>
    );
  }

  const cs = data.customer_service || {};
  const header = pickHeader(seed || code, "airline");

  return (
    <div className="border border-zinc-300 bg-white" data-testid="ai-profile-section">
      <div className="relative h-44 overflow-hidden">
        <img src={header} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-sky-900/60" />
        {data.tagline && (
          <div className="absolute bottom-5 left-6 right-6">
            <p className="text-white font-heading font-900 text-xl sm:text-2xl leading-snug drop-shadow-[0_1px_6px_rgba(0,0,0,0.5)]">{data.tagline}</p>
          </div>
        )}
      </div>

      <div className="p-6 lg:p-8">
        <div className="flex flex-wrap gap-2 mb-5">
          {data.founded && <Chip icon={<CalendarDays className="w-4 h-4" />} label={`Founded ${data.founded}`} />}
          {data.headquarters && <Chip icon={<Building2 className="w-4 h-4" />} label={data.headquarters} />}
          {data.alliance && <Chip icon={<Users className="w-4 h-4" />} label={data.alliance} />}
        </div>

        {data.overview && <div className="prose max-w-none whitespace-pre-line leading-relaxed text-zinc-800 text-[16px]">{data.overview}</div>}

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {data.hubs_summary && <Fact title="Hubs" body={data.hubs_summary} color="text-blue-600" border="border-blue-500" />}
          {data.fleet_summary && <Fact title="Fleet" body={data.fleet_summary} color="text-rose-600" border="border-rose-500" />}
        </div>

        {data.notable_facts?.length > 0 && (
          <div className="mt-6">
            <div className="font-heading font-900 text-emerald-600 text-lg mb-2">Notable facts</div>
            <ul className="space-y-1.5">
              {data.notable_facts.map((f, i) => (
                <li key={i} className="flex gap-2 text-base text-zinc-800"><span className="text-emerald-600 font-mono font-700">›</span>{f}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="border border-zinc-300 p-4">
            <div className="font-heading font-900 text-violet-600 text-lg mb-3">Contact</div>
            <div className="space-y-2 text-base">
              <Row icon={<Phone className="w-4 h-4" />} value={cs.phone} testid="ai-cs-phone" />
              <Row icon={<Mail className="w-4 h-4" />} value={cs.email} testid="ai-cs-email" />
              <Row icon={<Globe className="w-4 h-4" />} value={cs.website} href={cs.website} testid="ai-cs-website" />
            </div>
          </div>
          <div className="border border-zinc-300 p-4">
            <div className="font-heading font-900 text-orange-600 text-lg mb-3">Reliable links</div>
            <div className="space-y-2 text-base">
              {data.links?.official && <LinkRow href={data.links.official} label="Official website" />}
              <LinkRow href={data.links?.wikipedia} label="Search on Wikipedia" />
              <LinkRow href={data.links?.google} label="Search on Google" />
            </div>
          </div>
        </div>

        <div className="mt-5 flex gap-2 items-start text-sm text-amber-700 bg-amber-50 border border-amber-200 p-3" data-testid="ai-disclaimer">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{data.disclaimer}</span>
        </div>
      </div>
    </div>
  );
}

const Chip = ({ icon, label }) => (
  <span className="inline-flex items-center gap-1.5 border border-zinc-300 px-3 py-1.5 text-sm font-600">{icon}{label}</span>
);
const Fact = ({ title, body, color = "text-blue-600", border = "border-blue-500" }) => (
  <div className={`border-l-4 ${border} pl-3`}>
    <div className={`font-heading font-900 text-lg ${color}`}>{title}</div>
    <div className="text-base text-zinc-800 mt-0.5">{body}</div>
  </div>
);
const Row = ({ icon, value, href, testid }) => (
  <div className="flex items-center gap-2 text-zinc-700" data-testid={testid}>
    <span className="text-primary">{icon}</span>
    {value ? (href ? <a href={href} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{value}</a> : <span className="break-all">{value}</span>)
      : <span className="text-muted-foreground italic">Not available</span>}
  </div>
);
const LinkRow = ({ href, label }) => (
  <a href={href} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-primary hover:underline">
    <ExternalLink className="w-4 h-4" /> {label}
  </a>
);
